import { kindLabel } from '../sync/syncEngine';
import type { PunchEcho } from '../types';

interface PunchOverlayProps {
  echo: PunchEcho | null;
  /** Tap-to-dismiss, so the next person in line need not wait out the timer. */
  onDismiss: () => void;
}

/**
 * The outcome of the most recent punch, shown so it can be read from across the
 * room: a full-screen GREEN overlay once the server confirms the clock in/out, or
 * a full-screen RED one when it rejects the punch (e.g. unrecognized PIN).
 *
 * The pending "confirming…" state is deliberately NOT an overlay — offline it can
 * last until the network returns, and it must never lock the keypad. The clock
 * screen shows it as a small pill instead. The engine auto-dismisses ok/rejected.
 */
export function PunchOverlay({ echo, onDismiss }: PunchOverlayProps): JSX.Element | null {
  if (echo === null || echo.pending) {
    return null;
  }

  if (echo.rejected) {
    return (
      <div className="overlay overlay--error" role="alert" aria-live="assertive" onClick={onDismiss}>
        <div className="overlay__icon" aria-hidden="true">
          ✕
        </div>
        <div className="overlay__title">Something went wrong</div>
        <div className="overlay__detail">{echo.message}</div>
      </div>
    );
  }

  return (
    <div className="overlay overlay--ok" role="status" aria-live="polite" onClick={onDismiss}>
      <div className="overlay__icon" aria-hidden="true">
        ✓
      </div>
      <div className="overlay__title">{echo.kind === null ? 'Recorded' : kindLabel(echo.kind)}</div>
      {echo.employeeName !== null && <div className="overlay__detail">{echo.employeeName}</div>}
    </div>
  );
}
